import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LinkIcon, CheckIcon, XMarkIcon } from './Icons';
import { updateResourceVisibility, searchUsersByEmail, fetchGroupsMine, getShareLink } from '../../services/apiService';
import { Class } from '../../types';
import { useGuestGate } from './GuestGateContext';

type Visibility = 'private' | 'link' | 'public' | 'restricted';
type ScopeType = 'class' | 'group' | 'users' | 'class_teachers';

export type BuildShareLinkParams = {
  resourceId: string | number;
  resourceTitle: string;
  token?: string | null;
};

type LinkVariant = 'detail' | 'html5';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: string;
  resourceId: string | number;
  resourceTitle: string;
  currentVisibility?: string;
  currentShareToken?: string | null;
  currentShareScope?: { type: string; ids: number[] } | null;
  currentTargetRoles?: string[];
  classes?: Class[];
  user?: { id?: string; email?: string; memberOfGroupId?: string | null; groupMembers?: { email: string }[] } | null;
  onUpdated?: (result: any) => void;
  onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
  /**
   * Hàm sinh URL trực tiếp đến file HTML5 (ví dụ /api/edu/view-simulation/{id}).
   * Khi có hàm này, modal hiển thị 2 tab: «Trang chi tiết» (link `/chia-se/...`) và «File HTML5» (link viewer trực tiếp),
   * giúp giáo viên chọn loại link phù hợp với nhu cầu (gửi học sinh chơi luôn, hoặc chia sẻ trang giới thiệu trên MXH).
   */
  buildShareLink?: (params: BuildShareLinkParams) => string;
  /** Mặc định loại link hiển thị trước (chỉ áp dụng khi có buildShareLink). Mặc định: 'detail'. */
  defaultLinkVariant?: LinkVariant;
}

const RESOURCE_SLUGS: Record<string, string> = {
  documents: 'documents',
  lessonPlans: 'lesson-plans',
  'drawing-lessons': 'drawing-lessons',
  quizzes: 'quizzes',
  presentations: 'presentations',
  games: 'games',
  homeworks: 'homeworks',
  activities: 'activities',
  'scientific-simulations': 'scientific-simulations',
  videoProjects: 'video-projects',
  thietKe3D: 'thiet-ke-3d',
  'ai-fact-check-sessions': 'ai-fact-check-sessions',
  'ai-ethics-challenge-sessions': 'ai-ethics-challenge-sessions',
  'ai-lab-sessions': 'ai-lab-sessions',
};

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  resourceType,
  resourceId,
  resourceTitle,
  currentVisibility = 'private',
  currentShareToken,
  currentShareScope,
  currentTargetRoles,
  classes = [],
  user,
  onUpdated,
  onShowToast,
  buildShareLink,
  defaultLinkVariant = 'detail',
}) => {
  const [visibility, setVisibility] = useState<Visibility>((currentVisibility as Visibility) || 'private');
  const [targetRoles, setTargetRoles] = useState<string[]>(currentTargetRoles || ['all']);
  const [expiresIn, setExpiresIn] = useState<string>('7');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToken, setActiveToken] = useState<string | null>(currentShareToken ?? null);
  const [linkVariant, setLinkVariant] = useState<LinkVariant>(defaultLinkVariant);
  const [copied, setCopied] = useState(false);
  const { isGuest, requireLogin } = useGuestGate();

  // Chế độ khách: chặn chia sẻ → mời đăng nhập, đóng modal.
  useEffect(() => {
    if (isOpen && isGuest) {
      requireLogin('chia sẻ');
      onClose();
    }
  }, [isOpen, isGuest]);

  const slug = RESOURCE_SLUGS[resourceType] || resourceType;
  const hasHtml5Variant = Boolean(buildShareLink);

  // Sinh URL chia sẻ tương ứng với loại link đang chọn (chi tiết hoặc HTML5).
  // Việc tách thành derived value giúp khi user đổi tab / lưu chế độ chia sẻ là URL được cập nhật ngay.
  const shareLink = React.useMemo<string | null>(() => {
    if (visibility !== 'link' && visibility !== 'public') return null;
    const titleStr = typeof resourceTitle === 'string' ? resourceTitle : '';
    const detailLink = getShareLink(slug, resourceId, titleStr, activeToken ?? null);
    if (linkVariant === 'html5' && buildShareLink) {
      return buildShareLink({ resourceId, resourceTitle: titleStr, token: activeToken ?? null });
    }
    return detailLink;
  }, [visibility, linkVariant, resourceId, resourceTitle, slug, activeToken, buildShareLink]);

  useEffect(() => {
    setActiveToken(currentShareToken ?? null);
  }, [currentShareToken]);

  useEffect(() => {
    if (isOpen) {
      setLinkVariant(defaultLinkVariant);
    }
  }, [isOpen, defaultLinkVariant]);

  const [scopeType, setScopeType] = useState<ScopeType>('class');
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);
  // Chia sẻ với đồng nghiệp cùng lớp (GVCN + GV bộ môn dùng chung roster) — chỉ chọn 1 lớp.
  const [selectedTeacherClassId, setSelectedTeacherClassId] = useState<number | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{ id: number; email: string; name?: string }[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<{ id: number; email: string; name?: string }[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    fetchGroupsMine().then(setGroups).catch(() => setGroups([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Chỉ cập nhật state từ props khi modal vừa được mở
    // hoặc khi props thay đổi thực sự từ bên ngoài (không phải do đang lưu)
    if (!loading) {
      setVisibility((currentVisibility as Visibility) || 'private');
      setTargetRoles(currentTargetRoles && currentTargetRoles.length ? currentTargetRoles : ['all']);
      setError(null);
      setCopied(false);
    }
  }, [isOpen, currentVisibility, currentTargetRoles]);

  const handleUserSearch = useCallback(async () => {
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      return;
    }
    const results = await searchUsersByEmail(userSearchQuery);
    setUserSearchResults(results.filter(u => !selectedUsers.some(s => s.id === u.id)));
  }, [userSearchQuery, selectedUsers]);

  useEffect(() => {
    const t = setTimeout(handleUserSearch, 300);
    return () => clearTimeout(t);
  }, [userSearchQuery, handleUserSearch]);

  useEffect(() => {
    if (currentShareScope) {
      setScopeType((currentShareScope.type as ScopeType) || 'class');
      setSelectedClassIds(currentShareScope.type === 'class' ? currentShareScope.ids : []);
      setSelectedGroupIds(currentShareScope.type === 'group' ? currentShareScope.ids : []);
      setSelectedUserIds(currentShareScope.type === 'users' ? currentShareScope.ids : []);
      setSelectedTeacherClassId(currentShareScope.type === 'class_teachers' ? (currentShareScope.ids[0] ?? null) : null);
      setSelectedUsers([]);
    }
  }, [currentShareScope]);

  const addUser = (u: { id: number; email: string; name?: string }) => {
    if (selectedUsers.some(s => s.id === u.id)) return;
    setSelectedUsers([...selectedUsers, u]);
    setSelectedUserIds([...selectedUserIds, u.id]);
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  const removeUser = (id: number) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== id));
    setSelectedUserIds(selectedUserIds.filter(i => i !== id));
  };

  const toggleClass = (id: number) => {
    const numId = (typeof id === 'string' ? parseInt(id, 10) : id) || 0;
    setSelectedClassIds(prev => prev.includes(numId) ? prev.filter(i => i !== numId) : [...prev, numId]);
  };

  const toggleGroup = (id: number) => {
    setSelectedGroupIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const buildShareScope = (): { type: string; ids: number[] } | null => {
    if (scopeType === 'class' && selectedClassIds.length > 0) {
      return { type: 'class', ids: selectedClassIds };
    }
    if (scopeType === 'group' && selectedGroupIds.length > 0) {
      return { type: 'group', ids: selectedGroupIds };
    }
    if (scopeType === 'users' && selectedUserIds.length > 0) {
      return { type: 'users', ids: selectedUserIds };
    }
    if (scopeType === 'class_teachers' && selectedTeacherClassId != null) {
      return { type: 'class_teachers', ids: [selectedTeacherClassId] };
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    setLoading(true);
    try {
      const payload: any = { 
        visibility,
        targetRoles: targetRoles.includes('all') ? ['all'] : targetRoles
      };
      if (visibility === 'link') {
        const days = parseInt(expiresIn, 10) || 0;
        payload.shareExpiresAt = days > 0
          ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
          : null;
      }
      if (visibility === 'restricted') {
        const scope = buildShareScope();
        if (!scope) {
          setError('Vui lòng chọn ít nhất một lớp, nhóm hoặc người dùng.');
          setLoading(false);
          return;
        }
        payload.shareScope = scope;
      }
      const result = await updateResourceVisibility(slug, resourceId, payload);
      const token = result.shareToken ?? result.share_token ?? null;
      setActiveToken(token);

      if (onShowToast) {
        onShowToast('Cập nhật thiết lập chia sẻ thành công!', 'success');
      }

      // Cập nhật state local ngay lập tức để tránh bị useEffect đè lại giá trị cũ từ props
      setVisibility(result.visibility || visibility);
      if (result.targetRoles || result.target_roles) {
        setTargetRoles(result.targetRoles || result.target_roles);
      }

      onUpdated?.(result);
    } catch (e: any) {
      setError(e.message || 'Lỗi cập nhật');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareLink;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen || isGuest) return null;

  const modalBody = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 sm:p-6"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[min(90vh,880px)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        <div className="shrink-0 flex items-start justify-between gap-3 px-6 pt-6 pb-3 border-b border-slate-100">
          <h2 id="share-modal-title" className="text-lg font-bold text-slate-800 flex items-start gap-2 pr-2 leading-snug">
            <LinkIcon className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
            <span>
              Chia sẻ:{' '}
              {typeof resourceTitle === 'string'
                ? resourceTitle
                : resourceTitle && typeof resourceTitle === 'object'
                  ? String((resourceTitle as any).title ?? (resourceTitle as any).name ?? '')
                  : 'Tài nguyên'}
            </span>
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1" aria-label="Đóng">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-4">
          {hasHtml5Variant && (
            <div className="text-xs text-slate-600 leading-relaxed rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 space-y-2">
              <p>
                <strong className="text-slate-700">Hai loại link để chọn:</strong>
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  <strong className="text-slate-700">Trang chi tiết</strong> — link đẹp, có hình preview &amp; mô tả khi chia sẻ lên Facebook/Zalo. Người xem sẽ vào trang chi tiết của tài nguyên (lưu được vào thư viện, đánh giá, ...).
                </li>
                <li>
                  <strong className="text-slate-700">File HTML5</strong> — mở thẳng vào nội dung tương tác (mô phỏng/3D/trình chiếu). Phù hợp khi gửi học sinh chơi luôn hoặc nhúng vào LMS, slide bài giảng.
                </li>
              </ul>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Chế độ chia sẻ</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as Visibility)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-800"
            >
              <option value="private">Chỉ mình tôi</option>
              <option value="link">Chia sẻ qua link</option>
              <option value="public">Công khai</option>
              <option value="restricted">Chia sẻ với (lớp / nhóm / người dùng)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tài nguyên dành cho</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'Tất cả' },
                { value: 'teacher', label: 'Giáo viên' },
                { value: 'student', label: 'Học sinh' },
                { value: 'parent', label: 'Phụ huynh' },
              ].map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => {
                    if (r.value === 'all') {
                      setTargetRoles(['all']);
                    } else {
                      const newRoles = targetRoles.includes('all') ? [r.value] : (
                        targetRoles.includes(r.value)
                          ? targetRoles.filter(v => v !== r.value)
                          : [...targetRoles, r.value]
                      );
                      setTargetRoles(newRoles.length === 0 ? ['all'] : newRoles);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                    targetRoles.includes(r.value)
                      ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-teal-200 hover:text-teal-600'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2 italic">
              * Giúp hệ thống phân loại và đề xuất tài nguyên cho đúng đối tượng.
            </p>
          </div>

          {visibility === 'link' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Link hết hạn sau (ngày)</label>
              <select
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-800"
              >
                <option value="1">1 ngày</option>
                <option value="7">7 ngày</option>
                <option value="30">30 ngày</option>
                <option value="0">Không hết hạn</option>
              </select>
            </div>
          )}

          {visibility === 'restricted' && (
            <div className="space-y-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
              <p className="text-[11px] text-slate-500 leading-snug">
                Nhóm dùng cho chia sẻ là các nhóm bạn làm chủ trong mục Cài đặt (Quản lý Nhóm &amp; Tín dụng). Thêm thành viên vào nhóm để họ nhận được tài nguyên gán cho nhóm; việc chia credit là quyền riêng của các gói Team/Org.
              </p>
              <label className="block text-sm font-medium text-slate-700">Chia sẻ với</label>
              <select
                value={scopeType}
                onChange={(e) => setScopeType(e.target.value as ScopeType)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-800"
              >
                <option value="class">Lớp học (Học sinh/Phụ huynh)</option>
                <option value="class_teachers">Đồng nghiệp cùng lớp (Giáo viên)</option>
                <option value="group">Nhóm</option>
                <option value="users">Người dùng (email)</option>
              </select>

              {scopeType === 'class' && (
                <div className="space-y-2">
                  {classes.length === 0 ? (
                    <p className="text-sm text-slate-500">Bạn chưa có lớp nào. Tạo lớp trong Quản lý lớp học.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {classes.map((c) => {
                        const id = typeof c.id === 'string' ? parseInt(c.id, 10) : (c.id as number);
                        const checked = selectedClassIds.includes(id);
                        return (
                          <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleClass(id)}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm">{c.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {scopeType === 'class_teachers' && (
                <div className="space-y-2">
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Tài nguyên sẽ hiển thị cho TẤT CẢ giáo viên đang active của 1 lớp bạn chọn (GVCN + GV bộ môn dùng chung roster) — không phân biệt quyền quản trị hay dạy.
                  </p>
                  {classes.length === 0 ? (
                    <p className="text-sm text-slate-500">Bạn chưa có lớp nào. Tạo lớp trong Quản lý lớp học.</p>
                  ) : (
                    <div className="flex flex-col gap-2 max-h-40 overflow-y-auto pr-1">
                      {classes.map((c) => {
                        const id = typeof c.id === 'string' ? parseInt(c.id, 10) : (c.id as number);
                        return (
                          <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="shareTeacherClass"
                              checked={selectedTeacherClassId === id}
                              onChange={() => setSelectedTeacherClassId(id)}
                              className="border-slate-300"
                            />
                            <span className="text-sm">
                              {c.name}
                              {typeof c.teacherCount === 'number' && c.teacherCount > 1 && (
                                <span className="ml-1 text-[10px] text-slate-400">({c.teacherCount} GV)</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {scopeType === 'group' && (
                <div className="space-y-2">
                  {groups.length === 0 ? (
                    <p className="text-sm text-slate-600">
                      Bạn chưa có nhóm phù hợp để chia sẻ học liệu. Tạo nhóm và mời thành viên trong phần{' '}
                      <span className="font-semibold text-slate-700">Cài đặt → Quản lý Nhóm &amp; Tín dụng</span>, sau đó quay lại để chọn nhóm tại đây.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                      {groups.map((g) => (
                        <label key={g.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedGroupIds.includes(g.id)}
                            onChange={() => toggleGroup(g.id)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">{g.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {scopeType === 'users' && (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Tìm theo email..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                  {userSearchResults.length > 0 && (
                    <div className="border border-slate-200 rounded-lg divide-y max-h-32 overflow-y-auto">
                      {userSearchResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => addUser(u)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50"
                        >
                          {u.email} {u.name ? `(${u.name})` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedUsers.map((u) => (
                        <span
                          key={u.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 rounded text-sm"
                        >
                          {u.email}
                          <button type="button" onClick={() => removeUser(u.id)} className="text-teal-600 hover:text-teal-800">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <p className="text-red-600 text-sm">{error}</p>}

          {shareLink && (
            <div className="space-y-2 p-3 bg-teal-50 rounded-lg border border-teal-100">
              {hasHtml5Variant && (
                <div className="inline-flex w-full sm:w-auto p-0.5 rounded-lg bg-white border border-teal-200/80 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setLinkVariant('detail')}
                    className={`flex-1 sm:flex-initial px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                      linkVariant === 'detail'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-teal-700'
                    }`}
                    title="Link tới trang chi tiết, có preview hình + mô tả khi chia sẻ trên MXH"
                  >
                    Trang chi tiết
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkVariant('html5')}
                    className={`flex-1 sm:flex-initial px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                      linkVariant === 'html5'
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-teal-700'
                    }`}
                    title="Link mở thẳng file HTML5 tương tác, phù hợp gửi học sinh chơi luôn / nhúng iframe"
                  >
                    File HTML5
                  </button>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  readOnly
                  value={shareLink}
                  className="flex-1 min-w-0 text-sm text-slate-700 bg-white border border-teal-200/80 rounded px-2 py-2"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex shrink-0 items-center justify-center gap-1 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
                >
                  {copied ? <CheckIcon className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                  {copied ? 'Đã copy' : 'Copy'}
                </button>
              </div>
              {hasHtml5Variant && (
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  {linkVariant === 'detail'
                    ? 'Link này phù hợp khi đăng lên mạng xã hội — sẽ hiển thị thumbnail, tiêu đề và mô tả tài nguyên.'
                    : 'Link này mở thẳng nội dung HTML5 tương tác — phù hợp gửi học sinh chơi luôn hoặc nhúng iframe vào LMS / Google Sites / Padlet.'}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 flex gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/80 rounded-b-2xl">
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Đang xử lý...' : 'Lưu'}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-100 bg-white">
            Đóng
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;

  return createPortal(modalBody, document.body);
};
